"use client";

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import Icon from "./ui/Icon";
import { EASE } from "./ui/Reveal";

const SECTIONS = [
  { key: "management", to: "management" },
  { key: "marketplace", to: "marketplace" },
  { key: "fintech", to: "fintech" },
  { key: "pricing", to: "pricing" },
  { key: "about", to: "about" },
];

const ROUTES = [{ key: "downloads", to: "/downloads" }];

const MANAGEMENT_URL = "https://management.cosebu.com/";

const Wordmark = ({ onClick }) => (
  <RouterLink
    to="/"
    onClick={onClick}
    className="flex items-center gap-2.5 rounded-sm"
    aria-label="COSEBU — home"
  >
    <img
      src="/cosebu-logo.png"
      alt=""
      width={28}
      height={28}
      className="h-7 w-7 object-contain"
    />
    <span className="text-[17px] font-semibold tracking-[-0.02em]">COSEBU</span>
  </RouterLink>
);

const Navbar = () => {
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const reduced = useReducedMotion();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the sheet on navigation and lock the page behind it.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => event.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const linkClass = (isActive, mobile) =>
    [
      "relative inline-flex items-center rounded-sm transition-colors duration-150",
      mobile
        ? "h-12 w-full justify-between px-1 text-[16px]"
        : "h-9 px-3 text-[14px]",
      isActive ? "text-ink" : "text-ink-2 hover:text-ink",
    ].join(" ");

  const underline = (
    <motion.span
      layoutId="nav-active"
      transition={{ duration: 0.3, ease: EASE }}
      className="absolute inset-x-3 -bottom-[13px] h-px bg-ink"
    />
  );

  const renderSectionLink = (item, { onClick, mobile } = {}) => {
    const isActive = isHome && active === item.to;
    const label = t.nav[item.key];
    const chevron = mobile ? (
      <Icon name="arrowRight" size={16} className="text-ink-3" />
    ) : null;

    if (isHome) {
      return (
        <ScrollLink
          to={item.to}
          smooth
          spy
          offset={-72}
          duration={reduced ? 0 : 420}
          onSetActive={() => setActive(item.to)}
          onClick={onClick}
          className={`${linkClass(isActive, mobile)} cursor-pointer`}
          aria-current={isActive ? "true" : undefined}
        >
          {label}
          {chevron}
          {isActive && !mobile ? underline : null}
        </ScrollLink>
      );
    }

    return (
      <RouterLink
        to={`/#${item.to}`}
        onClick={onClick}
        className={linkClass(false, mobile)}
      >
        {label}
        {chevron}
      </RouterLink>
    );
  };

  const renderRouteLink = (item, { onClick, mobile } = {}) => {
    const isActive = location.pathname === item.to;
    return (
      <RouterLink
        to={item.to}
        onClick={onClick}
        className={linkClass(isActive, mobile)}
        aria-current={isActive ? "page" : undefined}
      >
        {t.nav[item.key]}
        {mobile ? <Icon name="arrowRight" size={16} className="text-ink-3" /> : null}
        {isActive && !mobile ? underline : null}
      </RouterLink>
    );
  };

  return (
    <header
      className={[
        "fixed inset-x-0 top-0 z-50 border-b transition-colors duration-200",
        scrolled || menuOpen ? "nav-surface border-line" : "border-transparent bg-transparent",
      ].join(" ")}
    >
      <div className="shell flex h-[68px] items-center justify-between gap-6">
        <Wordmark onClick={closeMenu} />

        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-0.5">
            {SECTIONS.slice(0, 3).map((item) => (
              <li key={item.key}>{renderSectionLink(item)}</li>
            ))}
            {ROUTES.map((item) => (
              <li key={item.key}>{renderRouteLink(item)}</li>
            ))}
            {SECTIONS.slice(3).map((item) => (
              <li key={item.key}>{renderSectionLink(item)}</li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleLanguage}
            className="btn btn--ghost h-9 px-2.5 font-mono text-[12px] uppercase tracking-[0.08em]"
            aria-label={t.ui.switchLanguage}
            title={t.ui.switchLanguage}
          >
            {language === "en" ? "EN" : "FR"}
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            className="btn btn--ghost h-9 w-9 px-0"
            aria-label={isDark ? t.ui.toLight : t.ui.toDark}
            title={isDark ? t.ui.toLight : t.ui.toDark}
          >
            <Icon name={isDark ? "sun" : "moon"} size={18} />
          </button>

          <a
            href={MANAGEMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary btn--sm ml-1 hidden sm:inline-flex"
          >
            {t.management.tryProduct}
          </a>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="btn btn--ghost ml-0.5 h-9 w-9 px-0 lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? t.ui.closeMenu : t.ui.openMenu}
          >
            <Icon name={menuOpen ? "close" : "menu"} size={20} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            id="mobile-nav"
            initial={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, height: "auto" }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.26, ease: EASE }}
            className="overflow-hidden border-t border-line bg-paper lg:hidden"
          >
            <nav aria-label="Mobile" className="shell py-2">
              <ul className="divide-rule">
                {[...SECTIONS.slice(0, 3), ...ROUTES, ...SECTIONS.slice(3)].map((item) => (
                  <li key={item.key} className="flex">
                    {item.to.startsWith("/")
                      ? renderRouteLink(item, { onClick: closeMenu, mobile: true })
                      : renderSectionLink(item, { onClick: closeMenu, mobile: true })}
                  </li>
                ))}
              </ul>

              <a
                href={MANAGEMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary btn--block my-4"
                onClick={closeMenu}
              >
                {t.management.tryProduct}
                <Icon name="arrowUpRight" size={16} />
              </a>
            </nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
